import { useAiAssistantStore } from '~/stores/aiAssistantStore';
import { useAiAssistant } from '~/composables/useAiAssistant';
import { AI_ENDPOINTS, useAiEndpoints } from '~/composables/useAiEndpoints';
import { useListen, useIgnore } from '~/composables/useEventBus';

export interface AgentConversationTokenUsage {
    prompt_tokens: number;
    completion_tokens: number;
    cache_write_input_tokens: number;
    cache_read_input_tokens: number;
}

export interface AgentConversationListItem {
    id: string;
    title: string | null;
    last_agent_key: string | null;
    created_at: string;
    updated_at: string;
    token_usage?: AgentConversationTokenUsage;
}

interface AgentConversationEntity {
    type: string;
    id: string;
    title: string | null;
}

export const useAiAssistantPage = () => {
    const store = useAiAssistantStore();
    const { subscribeToChannel, unsubscribeFromChannel, abort } = useAiAssistant();
    const { url, getHeaders } = useAiEndpoints();
    const { $tracker } = useNuxtApp();

    const agentConversations = ref<AgentConversationListItem[]>([]);
    const currentAgentConversationId = ref<string | null>(null);
    const agentConversationEntities = ref<AgentConversationEntity[]>([]);
    const isLoadingAgentConversations = ref(false);
    const isLoadingAgentConversation = ref(false);
    const hasMoreAgentConversations = ref(false);
    const nextCursor = ref<string | null>(null);

    const fetchAgentConversations = async (cursor?: string): Promise<{ data: AgentConversationListItem[]; next_cursor: string | null }> => {
        const endpoint = cursor
            ? `${AI_ENDPOINTS.conversations}?cursor=${encodeURIComponent(cursor)}`
            : AI_ENDPOINTS.conversations;

        const res = await fetch(url(endpoint), { headers: getHeaders() });

        if (!res.ok) {
            return { data: [], next_cursor: null };
        }

        return await res.json();
    };

    const loadMoreAgentConversations = async () => {
        if (!hasMoreAgentConversations.value || isLoadingAgentConversations.value) return;

        isLoadingAgentConversations.value = true;

        try {
            const result = await fetchAgentConversations(nextCursor.value ?? undefined);
            agentConversations.value.push(...result.data);
            nextCursor.value = result.next_cursor;
            hasMoreAgentConversations.value = !!result.next_cursor;
        } finally {
            isLoadingAgentConversations.value = false;
        }
    };

    const loadAgentConversationEntities = async (id: string) => {
        try {
            const res = await fetch(url(AI_ENDPOINTS.conversationEntities(id)), {
                headers: getHeaders(),
            });

            if (res.ok) {
                const data = await res.json();
                // API returns { type, id, display_name }; the local model uses `title`.
                agentConversationEntities.value = (data.data ?? []).map((e: { type: string; id: string; display_name?: string | null; title?: string | null }) => ({
                    type: e.type,
                    id: e.id,
                    title: e.title ?? e.display_name ?? null,
                }));
            }
        } catch {
            agentConversationEntities.value = [];
        }
    };

    const loadAgentConversation = async (id: string) => {
        isLoadingAgentConversation.value = true;
        store.conversationId = id;
        currentAgentConversationId.value = id;

        try {
            const res = await fetch(url(AI_ENDPOINTS.conversationMessages(id)), {
                headers: getHeaders(),
            });

            if (res.ok) {
                const data = await res.json();
                store.loadHistory(data.data ?? []);
            }
        } catch {
            // Silently fail — show empty chat
        } finally {
            isLoadingAgentConversation.value = false;
        }

        await loadAgentConversationEntities(id);
    };

    const trackConversationCompleted = () => {
        if (!currentAgentConversationId.value) return;

        const conversation = agentConversations.value.find((c) => c.id === currentAgentConversationId.value);
        if (!conversation?.token_usage) return;

        const usage = conversation.token_usage;
        const totalTokens = usage.prompt_tokens + usage.completion_tokens + usage.cache_write_input_tokens + usage.cache_read_input_tokens;

        if (totalTokens === 0) return;

        $tracker.track('ai_conversation_completed', {
            conversation_id: conversation.id,
            agent_key: conversation.last_agent_key,
            prompt_tokens: usage.prompt_tokens,
            completion_tokens: usage.completion_tokens,
            cache_write_tokens: usage.cache_write_input_tokens,
            cache_read_tokens: usage.cache_read_input_tokens,
            total_tokens: totalTokens,
        });
    };

    const switchAgentConversation = async (id: string) => {
        trackConversationCompleted();
        abort();
        unsubscribeFromChannel();
        store.reset();
        agentConversationEntities.value = [];

        await loadAgentConversation(id);
        subscribeToChannel();
    };

    const createNewAgentConversation = () => {
        abort();
        unsubscribeFromChannel();
        store.reset();
        currentAgentConversationId.value = null;
        agentConversationEntities.value = [];
        subscribeToChannel();
    };

    const init = async (documentType?: string | null, documentId?: string | null, documentTitle?: string | null) => {
        isLoadingAgentConversations.value = true;

        try {
            const result = await fetchAgentConversations();
            agentConversations.value = result.data;
            nextCursor.value = result.next_cursor;
            hasMoreAgentConversations.value = !!result.next_cursor;
        } finally {
            isLoadingAgentConversations.value = false;
        }

        startListening();
        subscribeToChannel();

        if (documentType && documentId) {
            store.documentType = documentType;
            store.documentId = documentId;
            store.documentTitle = documentTitle ?? null;

            try {
                const params = new URLSearchParams({
                    conversable_type: documentType,
                    conversable_id: documentId,
                });

                const res = await fetch(`${url(AI_ENDPOINTS.forDocument)}?${params}`, {
                    headers: getHeaders(),
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data.conversation_id) {
                        await loadAgentConversation(data.conversation_id);
                        return;
                    }
                }
            } catch {
                // No existing agent conversation — contextual pill will show via store.documentType/documentTitle
            }
        }
    };

    const onEntityCreated = (payload: { type: string; id: string; display_name?: string }) => {
        if (!agentConversationEntities.value.some(e => e.type === payload.type && e.id === payload.id)) {
            agentConversationEntities.value.push({
                type: payload.type,
                id: payload.id,
                title: payload.display_name ?? null,
            });
        }
    };

    const onAgentConversationCreated = (payload: { id: string }) => {
        currentAgentConversationId.value = payload.id;
        if (!agentConversations.value.some(agentConversation => agentConversation.id === payload.id)) {
            agentConversations.value.unshift({
                id: payload.id,
                title: null,
                last_agent_key: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            });
        }
    };

    const onAgentConversationUpdated = (payload: { id: string; title: string | null }) => {
        const target = agentConversations.value.find((c) => c.id === payload.id);
        if (target) {
            target.title = payload.title;
        }
    };

    const startListening = () => {
        useListen('ai:entity-created', onEntityCreated);
        useListen('ai:conversation-created', onAgentConversationCreated);
        useListen('ai:conversation-updated', onAgentConversationUpdated);
    };

    const stopListening = () => {
        useIgnore('ai:entity-created', onEntityCreated);
        useIgnore('ai:conversation-created', onAgentConversationCreated);
        useIgnore('ai:conversation-updated', onAgentConversationUpdated);
    };

    const close = () => {
        trackConversationCompleted();
        abort();
        unsubscribeFromChannel();
        stopListening();
        store.reset();
    };

    return {
        agentConversations,
        currentAgentConversationId,
        agentConversationEntities,
        isLoadingAgentConversations,
        isLoadingAgentConversation,
        hasMoreAgentConversations,
        loadMoreAgentConversations,
        loadAgentConversation,
        switchAgentConversation,
        createNewAgentConversation,
        init,
        close,
    };
};
